import {useEffect, useState} from "react";
import CookieOutlined from "@mui/icons-material/CookieOutlined";
import {useMessages} from "i18n";
import {useConsent} from "consent/useConsent";
import {showsWithdrawalControl} from "consent/withdrawalControl";
import {CookieSettingsDialog} from "legal/CookieSettingsDialog";
import {LauncherButton} from "./CookieSettingsLauncher.styles";

/**
 * The always-present way back into the consent choices (kitecorp/transferify-ui#2675), mounted
 * app-wide in {@link RootLayout} beside the banner.
 *
 * One press from any page re-opens the Cookie Settings dialog, where each purpose can be granted or
 * withdrawn. Whether it shows at all is `consent/withdrawalControl`'s rule, with the reasoning; this
 * component renders a button and owns nothing else.
 *
 * It opens the SAME dialog the banner's Customize action and the Resources menu open, rather than a
 * second panel of its own: a third answer to "where are the cookie choices" is how the three drift
 * apart, and the legal bodies now name this control by the dialog's own title.
 *
 * The accessible name is `nav.resourcesMenu.cookieSettings` — the catalogue string the Resources row
 * already uses — so the two entry points cannot end up called different things in the three
 * locales, and the cookie policy's sentence about "the Cookie Settings button" stays true. `title`
 * carries it as well, because the glyph alone is not a label for a sighted pointer user.
 *
 * SSG-safe through the same `mounted` guard the banner uses, and for the same reason: `useConsent`
 * seeds its state from `getConsent()`, which in the BROWSER reads localStorage on the very first
 * render. A returning visitor who already decided would therefore hydrate a button the pre-rendered
 * HTML does not contain, which React rejects as a hydration mismatch and which takes the whole page
 * down with it (measured as React error #418 across 36 landing specs). Deferring to an effect keeps
 * this out of the static markup AND out of the first client render.
 */
export function CookieSettingsLauncher() {
	const {consent} = useConsent();
	const {nav} = useMessages();
	const [open, setOpen] = useState(false);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	if (!mounted || !showsWithdrawalControl(consent)) {
		return null;
	}

	return (
		<>
			<LauncherButton
				type="button"
				aria-label={nav.resourcesMenu.cookieSettings}
				title={nav.resourcesMenu.cookieSettings}
				onClick={() => setOpen(true)}
			>
				<CookieOutlined fontSize="small"/>
			</LauncherButton>
			<CookieSettingsDialog open={open} onClose={() => setOpen(false)}/>
		</>
	);
}
